import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/i18n";
import PublicLayout from "@/components/PublicLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import ScrollToTop from "@/components/ScrollToTop";

import Home from "@/pages/Home";
import MenuPage from "@/pages/Menu";
import Reservation from "@/pages/Reservation";
import Galerie from "@/pages/Galerie";
import Evenements from "@/pages/Evenements";
import Contact from "@/pages/Contact";
import Merci from "@/pages/Merci";
import EventRedirect from "@/pages/EventRedirect";
import Mariage from "@/pages/Mariage";
import ThemedWeddingPage from "@/pages/ThemedWeddingPage";
import { MARIAGE_HALAL_CONTENT } from "@/content/mariageHalal";
import { MARIAGE_JUIF_CONTENT } from "@/content/mariageJuif";
import Seminaire from "@/pages/Seminaire";
import Bapteme from "@/pages/Bapteme";
import Anniversaire from "@/pages/Anniversaire";
import CarteCadeau from "@/pages/CarteCadeau";
import CarteCadeauSucces from "@/pages/CarteCadeauSucces";

import AdminLogin from "@/pages/admin/Login";import AdminLayout from "@/pages/admin/Layout";
import AdminDashboard from "@/pages/admin/Dashboard";
import MenuManagement from "@/pages/admin/MenuManagement";
import GalleryAdmin from "@/pages/admin/GalleryAdmin";
import SeoPagesAdmin from "@/pages/admin/SeoPagesAdmin";
import PageEditor from "@/pages/admin/PageEditor";
import GiftCardsAdmin from "@/pages/admin/GiftCardsAdmin";
import HomeImagesAdmin from "@/pages/admin/HomeImagesAdmin";
import ReviewsAdmin from "@/pages/admin/ReviewsAdmin";
import { EventsAdmin, ContactAdmin } from "@/pages/admin/Inbox";
import Weddings from "@/pages/admin/Weddings";
import WeddingDetail from "@/pages/admin/WeddingDetail";
import WeddingEmailImport from "@/pages/admin/WeddingEmailImport";
import WeddingShell from "@/pages/wedding/WeddingShell";
import WeddingAccess from "@/pages/wedding/WeddingAccess";
import ClientWedding from "@/pages/wedding/ClientWedding";
import SeoPage from "@/pages/SeoPage";
import { MentionsLegales, PolitiqueConfidentialite, Cookies } from "@/pages/Legal";

import "@/App.css";

// Public routes that should also exist with /en prefix.
const PUBLIC_ROUTES = [
  { path: "/", element: <Home /> },
  { path: "/menu", element: <MenuPage /> },
  { path: "/reservation", element: <Reservation /> },
  { path: "/galerie", element: <Galerie /> },
  { path: "/evenements", element: <Evenements /> },
  { path: "/mariage", element: <Mariage /> },
  {
    path: "/mariage-halal-ville-davray",
    element: (
      <ThemedWeddingPage
        pageKey="mariage-halal"
        path="/mariage-halal-ville-davray"
        contentByLang={MARIAGE_HALAL_CONTENT}
        service={{ name: "Réception de mariage halal à L'Entracte", serviceType: "Réception de mariage halal" }}
      />
    ),
  },
  {
    path: "/mariage-juif-ville-davray",
    element: (
      <ThemedWeddingPage
        pageKey="mariage-juif"
        path="/mariage-juif-ville-davray"
        contentByLang={MARIAGE_JUIF_CONTENT}
        service={{ name: "Réception de mariage juive à L'Entracte", serviceType: "Réception de mariage" }}
      />
    ),
  },
  { path: "/seminaire", element: <Seminaire /> },
  { path: "/bapteme", element: <Bapteme /> },
  { path: "/anniversaire", element: <Anniversaire /> },
  // Legacy seminar URL → /seminaire (a server-side 301 must also be configured)
  {
    path: "/organisez-vos-seminaires-dentreprise-a-ville-davray-dans-un-cadre-dexception",
    element: <EventRedirect to="/seminaire" label="Séminaire d'entreprise" />,
  },
  { path: "/contact", element: <Contact /> },
  { path: "/merci", element: <Merci /> },
  // Redirects for the previous per-form URLs (in case they are still linked or bookmarked)
  { path: "/evenements/merci", element: <Merci /> },
  { path: "/contact/merci", element: <Merci /> },
  { path: "/carte-cadeau", element: <CarteCadeau /> },
  { path: "/carte-cadeau/succes", element: <CarteCadeauSucces /> },
  { path: "/mentions-legales", element: <MentionsLegales /> },
  { path: "/politique-de-confidentialite", element: <PolitiqueConfidentialite /> },
  { path: "/cookies", element: <Cookies /> },
];

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <I18nProvider>
          <ScrollToTop />
          <AnalyticsTracker />
          <Routes>
            {/* Private organizer portal — own shell, no marketing layout, no analytics */}
            <Route element={<WeddingShell />}>
              <Route path="/espace-reception/acces" element={<WeddingAccess />} />
              <Route path="/espace-reception" element={<ClientWedding />} />
              {/* Legacy aliases: already-sent links keep working, fragment preserved */}
              <Route path="/espace-mariage/acces" element={<WeddingAccess />} />
              <Route path="/espace-mariage" element={<ClientWedding />} />
            </Route>

            <Route element={<PublicLayout />}>
              {PUBLIC_ROUTES.map((r) => (
                <Route key={r.path} path={r.path} element={r.element} />
              ))}
              {/* English locale — same components, i18n hook detects /en prefix */}
              <Route path="/en" element={<Home />} />
              {PUBLIC_ROUTES.filter((r) => r.path !== "/").map((r) => (
                <Route key={`en${r.path}`} path={`/en${r.path}`} element={r.element} />
              ))}
              {/* Dynamic SEO pages last (catch-all) */}
              <Route path="/:slug" element={<SeoPage />} />
              <Route path="/en/:slug" element={<SeoPage />} />
            </Route>

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="menu" element={<MenuManagement />} />
              <Route path="gallery" element={<GalleryAdmin />} />
              <Route path="home-images" element={<HomeImagesAdmin />} />
              <Route path="reviews" element={<ReviewsAdmin />} />
              <Route path="pages" element={<PageEditor />} />
              <Route path="content" element={<Navigate to="/admin/pages" replace />} />
              <Route path="page-images" element={<Navigate to="/admin/pages" replace />} />
              <Route path="seo" element={<SeoPagesAdmin />} />
              <Route path="giftcards" element={<GiftCardsAdmin />} />
              <Route path="events" element={<EventsAdmin />} />
              <Route path="receptions" element={<Weddings />} />
              <Route path="receptions/:id" element={<WeddingDetail />} />
              <Route path="reception-emails" element={<WeddingEmailImport />} />
              <Route path="weddings" element={<Navigate to="/admin/receptions" replace />} />
              <Route path="weddings/:id" element={<WeddingDetail />} />
              <Route path="wedding-emails" element={<Navigate to="/admin/reception-emails" replace />} />
              <Route path="messages" element={<ContactAdmin />} />
            </Route>
          </Routes>
        </I18nProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
